import { useState, useEffect, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Privacy from "@/pages/Privacy";
import Dashboard from "@/pages/Dashboard";
import SocialOverview from "@/pages/SocialOverview";
import ContentLibrary from "@/pages/ContentLibrary";
import ApprovalQueue from "@/pages/ApprovalQueue";
import ContentCalendar from "@/pages/ContentCalendar";
import Analytics from "@/pages/Analytics";
import Settings from "@/pages/Settings";
import AdsManager from "@/pages/AdsManager";
import Strategy from "@/pages/Strategy";

export const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("studio_token"));

  const verifyToken = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      const res = await api.get("/auth/verify");
      setUser(res.data.user);
    } catch {
      localStorage.removeItem("studio_token");
      setToken(null);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { verifyToken(); }, [verifyToken]);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    const { token: t, user: u } = res.data;
    localStorage.setItem("studio_token", t);
    setToken(t);
    setUser(u);
    return u;
  };

  const logout = () => {
    localStorage.removeItem("studio_token");
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0f1114]">
        <div className="spinner-gold" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster
          theme="dark"
          position="bottom-right"
          toastOptions={{
            style: {
              background: '#1a1d21',
              border: '1px solid #2a2e36',
              color: '#d4d4d8',
              fontFamily: "'DM Sans', sans-serif",
            },
          }}
        />
        <div className="film-grain" />
        <div className="monitor-glow" />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route index element={<Dashboard />} />
            <Route path="social" element={<SocialOverview />} />
            <Route path="library" element={<ContentLibrary />} />
            <Route path="queue" element={<ApprovalQueue />} />
            <Route path="approve" element={<ApprovalQueue />} />
            <Route path="calendar" element={<ContentCalendar />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="ads" element={<AdsManager />} />
            <Route path="strategy" element={<Strategy />} />
            <Route path="settings" element={<Settings />} />
          </Route>
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
